import React, { useEffect, useState } from 'react';
import { SlidersHorizontal, Loader2, Save, Calculator, ShieldAlert, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { DashboardLayout } from '../../app/layouts/DashboardLayout';
import { ADMIN_NAV } from '../../constants/nav';
import { adminGetFeatureAccess, adminSaveFeatureAccess, FeatureAccessConfig } from '../../features/journal/api/journal';

type FeatureKey = 'kalkulator_lot' | 'harga_pertahanan' | 'request_indikator';

const ROLE_LABELS: Record<string, string> = {
  member: 'Member', vip: 'VIP', mentor: 'Mentor', sales: 'Sales', webinar: 'Peserta Webinar', workshop: 'Peserta Workshop',
};

const FEATURES: { key: FeatureKey; label: string; icon: any; desc: string }[] = [
  { key: 'kalkulator_lot', label: 'Kalkulator Lot', icon: Calculator, desc: 'Menghitung ukuran lot aman berdasarkan risiko & ketahanan dana.' },
  { key: 'harga_pertahanan', label: 'Harga Pertahanan', icon: ShieldAlert, desc: 'Menghitung lot maksimal & kebutuhan hedging (Sustaining Power).' },
  { key: 'request_indikator', label: 'Request Indikator', icon: KeyRound, desc: 'Mengajukan akses indikator TradingView ke admin.' },
];

export default function AdminFeatureAccessPage() {
  const [cfg, setCfg] = useState<FeatureAccessConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminGetFeatureAccess().then(setCfg).catch((e) => toast.error('Gagal memuat', { description: e.message })).finally(() => setLoading(false));
  }, []);

  const toggle = (feature: FeatureKey, role: string) => {
    if (!cfg) return;
    const current = new Set(cfg[feature]);
    if (current.has(role)) current.delete(role); else current.add(role);
    setCfg({ ...cfg, [feature]: Array.from(current) });
  };

  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      await adminSaveFeatureAccess({ kalkulator_lot: cfg.kalkulator_lot, harga_pertahanan: cfg.harga_pertahanan, request_indikator: cfg.request_indikator });
      toast.success('Akses menu disimpan');
    } catch (e: any) { toast.error('Gagal menyimpan', { description: e.response?.data?.detail || e.message }); }
    finally { setSaving(false); }
  };

  return (
    <DashboardLayout navItems={ADMIN_NAV} title="Akses Menu Tools">
      <div className="max-w-3xl" data-testid="admin-feature-access-page">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-xl bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center"><SlidersHorizontal className="w-5 h-5 text-cyan-400" strokeWidth={1.5} /></div>
          <div><p className="text-xs uppercase tracking-[0.2em] text-cyan-400">Kontrol Akses</p><h2 className="text-2xl sm:text-3xl tracking-tight font-medium text-white">Akses Menu Tools</h2></div>
        </div>
        <p className="text-sm text-white/60 mb-6">Jurnal Trading terbuka untuk semua member. Pilih (checklist) role yang boleh mengakses tiap fitur di bawah ini. Admin selalu punya akses.</p>

        {loading || !cfg ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-cyan-400 animate-spin" /></div>
        ) : (
          <div className="space-y-5">
            {FEATURES.map((f) => (
              <div key={f.key} className="bg-white/[0.02] border border-white/10 rounded-2xl p-5" data-testid={`feature-${f.key}`}>
                <div className="flex items-center gap-3 mb-4">
                  <f.icon className="w-5 h-5 text-amber-400" strokeWidth={1.5} />
                  <div><p className="text-white font-medium">{f.label}</p><p className="text-xs text-white/40">{f.desc}</p></div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {cfg.all_roles.map((role) => {
                    const checked = cfg[f.key].includes(role);
                    return (
                      <button key={role} data-testid={`access-${f.key}-${role}`} onClick={() => toggle(f.key, role)}
                        className={`px-3.5 py-2 rounded-lg text-sm border transition-colors ${checked ? 'bg-cyan-400/15 border-cyan-400/50 text-cyan-300' : 'bg-white/[0.02] border-white/10 text-white/50 hover:text-white/80'}`}>
                        {checked ? '✓ ' : ''}{ROLE_LABELS[role] || role}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            <button data-testid="feature-access-save" onClick={save} disabled={saving} className="bg-cyan-400 text-[#050914] font-medium px-6 py-2.5 rounded-lg transition-colors hover:bg-cyan-300 disabled:opacity-50 flex items-center gap-2">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Simpan Perubahan
            </button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
